import { thinGeometry } from './route'
import type {
  DiaryEntry,
  DiaryTrack,
  LatLon,
  TrackElevation,
  TrackFix,
  TrackPart,
  TrackProfilePoint,
  TrackStop,
} from './types'

/**
 * Der aufgezeichnete Weg: eine GPX-Datei lesen und daraus ableiten, was
 * unterwegs tatsächlich passiert ist.
 *
 * Reine Funktionen, kein DOM, kein React. Die Bedienung steht in
 * features/diary, das Zeichnen in features/map, das Datenmodell in types.ts -
 * hier stehen nur die Regeln, und die sind ohne Browser prüfbar.
 *
 * Warum ein eigener Leser und kein DOMParser: die Tests laufen in der Umgebung
 * node, dort gibt es keinen. Eine zweite Testumgebung nur für diese Datei
 * wäre der eine Preis; der andere ist, dass ein vollständiger XML-Leser weit
 * mehr kann, als hier gebraucht wird - Entitäten, Dokumenttypen, Namensräume
 * -, und die Dateien kommen von fremden Programmen. Gebraucht werden aus einer
 * GPX-Datei genau drei Dinge: trkpt mit lat und lon, darin ele und time. Der
 * Leser unten kennt nur diese, überspringt Kommentare, CDATA-Abschnitte,
 * Verarbeitungsanweisungen und die Dokumenttypangabe unbesehen und wirft
 * einzelne unbrauchbare Punkte weg, statt die ganze Datei zu verlieren.
 *
 * Zwei verschiedene Arten von Schaden gibt es dabei, und sie werden verschieden
 * behandelt: ein einzelner unbrauchbarer Punkt kostet diesen Punkt und wird
 * gezählt (dropped), kaputtes Markup kostet den ganzen Rest der Datei und wird
 * gemeldet (truncated). Still bleibt keiner von beiden.
 */

/** Fehler mit einer Meldung, die man dem Nutzer zeigen kann. */
export class GpxError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'GpxError'
  }
}

/** Ein Punkt der Aufzeichnung, so wie er in der Datei steht. */
export interface GpxPoint {
  lat: number
  lon: number
  /** Zeitpunkt in Millisekunden seit 1970, oder null ohne brauchbares time. */
  time: number | null
  /** Höhe in Metern, oder null ohne brauchbares ele. */
  ele: number | null
}

/**
 * Warum der Leser vor dem Dateiende aufgegeben hat.
 *
 * Ein einzelner unbrauchbarer Punkt kostet nur diesen Punkt (dropped). Kaputtes
 * Markup dagegen - ein nie geschlossenes Anführungszeichen, ein offener
 * Kommentar - kostet alles, was dahinter steht, denn ab dort weiss niemand
 * mehr, was Element ist und was Text. Das darf nicht still passieren: eine halb
 * gelesene Datei sieht sonst aus wie eine ganz gelesene, mit selbstbewussten
 * Kilometern, die nur den Anfang der Fahrt meinen.
 */
export interface GpxTruncation {
  /** Der Grund als Halbsatz, klein geschrieben, für die Meldung. */
  reason: string
  /** Zeichenposition, an der der Leser aufgehört hat. */
  at: number
  /** Anteil der Datei davor, in Prozent, abgerundet. */
  readPercent: number
}

/** Was aus einer Datei herausgelesen wurde. */
export interface GpxFile {
  /**
   * Alle Punkte in Dateireihenfolge.
   *
   * Mehrere trk und mehrere trkseg werden dabei aneinandergehängt und nicht
   * getrennt gehalten. Eine Etappe ist eine Fahrt, und ob das Gerät dabei
   * einmal ausgegangen ist oder die App den Track in Stücke geschnitten hat,
   * ist eine Eigenschaft des Aufzeichnungsgeräts und keine der Fahrt. Die
   * Lücke dazwischen geht dabei nicht verloren: sie steht in den Zeitstempeln,
   * und die Stopperkennung sieht sie sich genauso an wie jede andere Pause -
   * stand das Fahrzeug, ist es ein Halt, ging es weiter, zählt die Strecke.
   */
  points: GpxPoint[]
  /** Zahl der trk-Elemente in der Datei. */
  tracks: number
  /** Zahl der trkseg, die mindestens einen brauchbaren Punkt geliefert haben. */
  segments: number
  /** Punkte, die übersprungen wurden, weil ihre Koordinate unbrauchbar war. */
  dropped: number
  /** Gesetzt, wenn der Durchlauf vor dem Dateiende abgebrochen ist. */
  truncated: GpxTruncation | null
}

// --- Der Leser --------------------------------------------------------------

/** Ein Element, wie der Durchlauf es findet. */
interface XmlTag {
  /** Name ohne Namensraum, klein geschrieben. */
  name: string
  /** Ein schliessendes Element, also </trkpt>. */
  closing: boolean
  /** Ein leeres Element, also <trkpt … />. */
  selfClosing: boolean
  /** Der Attributteil, unverarbeitet. */
  attrs: string
  /** Position des schliessenden Winkels. Dahinter beginnt der Textinhalt. */
  end: number
}

/**
 * Sucht das Ende eines Elements und beachtet dabei Anführungszeichen.
 *
 * Ein > innerhalb eines Attributwerts ist in XML erlaubt und beendet das
 * Element nicht. Ohne diese Unterscheidung risse ein solcher Wert das Element
 * mitten entzwei.
 */
function findTagEnd(xml: string, start: number): number {
  let quote = ''
  for (let i = start + 1; i < xml.length; i++) {
    const c = xml[i]!
    if (quote) {
      if (c === quote) quote = ''
      continue
    }
    if (c === '"' || c === "'") quote = c
    else if (c === '>') return i
  }
  return -1
}

/**
 * Steht in dem angebrochenen Element ein Anführungszeichen offen?
 *
 * Nur für die Meldung, und nur dann berechnet, wenn ohnehin abgebrochen wird:
 * ein nie geschlossenes Zitat und eine mitten im Element abgeschnittene Datei
 * sehen für findTagEnd gleich aus, sind für den Nutzer aber zwei verschiedene
 * Dinge - das eine ist ein kaputtes Programm am anderen Ende, das andere ein
 * abgebrochener Download.
 */
function offenesZitat(xml: string, start: number): boolean {
  let quote = ''
  for (let i = start + 1; i < xml.length; i++) {
    const c = xml[i]!
    if (quote) {
      if (c === quote) quote = ''
    } else if (c === '"' || c === "'") quote = c
  }
  return quote !== ''
}

/** Position hinter der nächsten Zeichenfolge, oder -1, wenn sie fehlt. */
function skipPast(xml: string, from: number, marker: string): number {
  const i = xml.indexOf(marker, from)
  return i < 0 ? -1 : i + marker.length
}

/**
 * Überspringt eine Deklaration, also <!DOCTYPE …>.
 *
 * Ein interner Teilsatz in eckigen Klammern darf selbst > enthalten und muss
 * deshalb mitgezählt werden. Gelesen wird davon nichts: eine Entität, die
 * dort erklärt wird, gilt in dieser Datei nicht - genau das hält die
 * bekannten Aufblähangriffe über verschachtelte Entitäten draussen.
 */
function skipDeclaration(xml: string, start: number): number {
  const end = xml.indexOf('>', start)
  if (end < 0) return -1
  const bracket = xml.indexOf('[', start)
  if (bracket < 0 || bracket > end) return end + 1
  const close = xml.indexOf(']', bracket)
  return close < 0 ? -1 : skipPast(xml, close, '>')
}

/** Name ohne Namensraum: aus gpx:trkpt wird trkpt. */
function localName(name: string): string {
  const colon = name.lastIndexOf(':')
  return (colon < 0 ? name : name.slice(colon + 1)).toLowerCase()
}

/** Der Zettel, auf dem der Durchlauf vermerkt, dass er aufgeben musste. */
interface XmlScan {
  truncated: GpxTruncation | null
}

/** Hält fest, wo und warum aufgehört wurde. Liefert nichts: der Aufrufer kehrt um. */
function abbrechen(scan: XmlScan, xml: string, at: number, reason: string): void {
  scan.truncated = {
    reason,
    at,
    readPercent: xml.length === 0 ? 0 : Math.floor((at / xml.length) * 100),
  }
}

/**
 * Läuft einmal durch die Datei und liefert jedes Element, das darin steht.
 *
 * Jeder vorzeitige Ausstieg vermerkt sich in scan. Früher endete der Durchlauf
 * an diesen Stellen einfach - das Ergebnis war eine halbe Datei, die aussah wie
 * eine ganze.
 */
function* xmlTags(xml: string, scan: XmlScan): Generator<XmlTag> {
  let i = 0
  for (;;) {
    const start = xml.indexOf('<', i)
    if (start < 0) return

    if (xml.startsWith('<!--', start)) {
      i = skipPast(xml, start + 4, '-->')
      if (i < 0) return abbrechen(scan, xml, start, 'ein Kommentar wird nicht geschlossen')
      continue
    }
    if (xml.startsWith('<![CDATA[', start)) {
      i = skipPast(xml, start + 9, ']]>')
      if (i < 0) return abbrechen(scan, xml, start, 'ein CDATA-Abschnitt wird nicht geschlossen')
      continue
    }
    if (xml.startsWith('<?', start)) {
      i = skipPast(xml, start + 2, '?>')
      if (i < 0) return abbrechen(scan, xml, start, 'eine Anweisung wird nicht geschlossen')
      continue
    }
    if (xml.startsWith('<!', start)) {
      i = skipDeclaration(xml, start)
      if (i < 0) return abbrechen(scan, xml, start, 'eine Dokumenttypangabe wird nicht geschlossen')
      continue
    }

    const end = findTagEnd(xml, start)
    if (end < 0) {
      return abbrechen(
        scan,
        xml,
        start,
        offenesZitat(xml, start)
          ? 'ein Anführungszeichen in einem Element wird nicht geschlossen'
          : 'die Datei hört mitten in einem Element auf',
      )
    }
    i = end + 1

    const head = xml.slice(start + 1, end)
    const closing = head.startsWith('/')
    const body = closing ? head.slice(1) : head
    const selfClosing = body.endsWith('/')
    const inner = selfClosing ? body.slice(0, -1) : body

    const match = /^\s*([^\s/>]+)/.exec(inner)
    if (!match) continue

    yield {
      name: localName(match[1]!),
      closing,
      selfClosing,
      attrs: inner.slice(match[0].length),
      end,
    }
  }
}

/** Die fünf vordefinierten Entitäten und Zeichenverweise, sonst nichts. */
function decodeText(text: string): string {
  if (!text.includes('&')) return text
  return text.replace(/&(#x?[0-9a-fA-F]+|amp|lt|gt|quot|apos);/g, (whole, name: string) => {
    if (name === 'amp') return '&'
    if (name === 'lt') return '<'
    if (name === 'gt') return '>'
    if (name === 'quot') return '"'
    if (name === 'apos') return "'"
    const code = name.startsWith('#x') ? parseInt(name.slice(2), 16) : Number(name.slice(1))
    return Number.isFinite(code) && code > 0 && code <= 0x10ffff
      ? String.fromCodePoint(code)
      : whole
  })
}

/** Der Text zwischen diesem Element und dem nächsten. */
function textAfter(xml: string, tag: XmlTag): string {
  const next = xml.indexOf('<', tag.end + 1)
  return decodeText(xml.slice(tag.end + 1, next < 0 ? xml.length : next)).trim()
}

/**
 * Ein Attribut als Zahl.
 *
 * Ein fehlendes Attribut ergibt NaN und nicht null: Number(null) wäre 0, und
 * damit hätte ein trkpt ohne lat stillschweigend den Äquator als Breite.
 */
function numberAttr(attrs: string, name: string): number {
  const pattern = new RegExp(`(?:^|\\s)(?:[\\w.-]+:)?${name}\\s*=\\s*("([^"]*)"|'([^']*)')`, 'i')
  const match = pattern.exec(attrs)
  const value = match?.[2] ?? match?.[3]
  return value === undefined || value.trim() === '' ? NaN : Number(value)
}

/**
 * Äusserste Höhen, die noch als Angabe durchgehen.
 *
 * Unterhalb des Toten Meeres und oberhalb des Mount Everest steht kein Camper.
 * Ein Ausreisser wie 99999 käme sonst als höchster Punkt in die Anzeige und
 * als tausend Meter Anstieg in die Summe.
 */
const ELE_MIN_M = -500
const ELE_MAX_M = 9000

function parseEle(text: string): number | null {
  const value = Number(text)
  if (!Number.isFinite(value) || value < ELE_MIN_M || value > ELE_MAX_M) return null
  return value
}

/**
 * Datum, Zeit und Zone eines Zeitstempels, so wie sie in der Datei stehen.
 *
 * Die Zeit ist bewusst nicht verpflichtend: ein Datum allein ist zwar kein
 * gültiges GPX, aber unmissverständlich, und es abzuweisen kostet einen
 * Zeitstempel, den man haben könnte.
 */
const ZEITSTEMPEL =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:[.,](\d+))?(Z|[+-]\d{2}(?::?\d{2})?)?)?$/i

/**
 * Der Versatz einer Zonenangabe in Minuten.
 *
 * Keine Angabe heisst 0, also UTC - das ist der Kern der Sache und steht
 * ausführlich über parseTime. null heisst unbrauchbar.
 */
function zonenversatz(zone: string | undefined): number | null {
  if (zone === undefined || zone === 'Z' || zone === 'z') return 0

  const ziffern = zone.slice(1).replace(':', '')
  const stunden = Number(ziffern.slice(0, 2))
  const minuten = ziffern.length > 2 ? Number(ziffern.slice(2)) : 0
  // Die Zeitzonen der Erde reichen von -12 bis +14, die Norm lässt genau das
  // zu. Alles darüber ist ein Tippfehler und keine Zone.
  if (stunden > 14 || minuten > 59) return null

  return (zone.startsWith('-') ? -1 : 1) * (stunden * 60 + minuten)
}

/**
 * Der Zeitstempel eines Punktes, in Millisekunden seit 1970.
 *
 * Von Hand gelesen und nicht über Date.parse, aus zwei Gründen.
 *
 * Der erste ist die fehlende Zonenangabe. GPX schreibt UTC vor, aber ein
 * Gerät, das "2026-08-17T07:30:00" ohne Z schreibt, ist keine Seltenheit - und
 * der Columbus hat obendrein eine eigene Zeitzoneneinstellung, von der die
 * Spezifikation nicht sagt, ob sie in die Datei wandert. Date.parse liest eine
 * Angabe ohne Zone nach ECMA-262 als Ortszeit des Geräts, auf dem gerade
 * importiert wird. Dieselbe Datei ergäbe damit in Bukarest eine andere Uhrzeit
 * als in Bern - eine Stunde, im Grenzfall einen Tag. Kilometer und Fahrzeit
 * blieben richtig, das sind Differenzen; falsch würden die angezeigte Uhrzeit
 * und das Datum, und das Datum entscheidet, auf welche Etappe ein Stück geht.
 * Fehlt die Zone, ist es hier UTC.
 *
 * Der zweite ist alles, was nicht genau nach ISO aussieht. Ein Leerzeichen
 * statt des T ("2026-08-17 07:30:00") steht nicht in der Norm, und was eine
 * Laufzeitumgebung damit macht, bleibt ihr überlassen: Chrome liest es als
 * Ortszeit, andere Umgebungen lesen es anders oder gar nicht. Ein Import, der
 * je nach Browser andere Zahlen liefert, ist nicht prüfbar - hier gilt für
 * das Leerzeichen dieselbe Regel wie für das T.
 *
 * Angenommen werden: Datum allein (Mitternacht UTC), Datum mit Zeit, mit oder
 * ohne Sekunden, mit Sekundenbruchteilen hinter Punkt oder Komma (ab der
 * vierten Stelle abgeschnitten, nicht gerundet) und mit Zone als Z, +HH:MM,
 * +HHMM oder +HH. Ein Datum, das es nicht gibt, fällt durch: der 31. Februar
 * ergibt keinen 3. März, sondern keinen Zeitstempel. Alles andere ebenso -
 * lieber kein Zeitstempel als ein geratener.
 */
export function parseTime(text: string): number | null {
  const m = ZEITSTEMPEL.exec(text.trim())
  if (!m) return null

  const jahr = Number(m[1])
  const monat = Number(m[2])
  const tag = Number(m[3])
  const stunde = m[4] === undefined ? 0 : Number(m[4])
  const minute = m[5] === undefined ? 0 : Number(m[5])
  const sekunde = m[6] === undefined ? 0 : Number(m[6])
  // Abgeschnitten und nicht gerundet: 1,9995 s soll nicht zu 2,000 s werden,
  // sondern zu 1,999 - eine Millisekunde ist hier ohnehin Zierrat.
  const bruch = m[7] === undefined ? 0 : Number(m[7].slice(0, 3).padEnd(3, '0'))

  const versatz = zonenversatz(m[8])
  if (versatz === null) return null
  if (monat < 1 || monat > 12 || tag < 1 || tag > 31) return null
  if (stunde > 23 || minute > 59 || sekunde > 59) return null

  let wanduhr = Date.UTC(jahr, monat - 1, tag, stunde, minute, sekunde, bruch)
  // Date.UTC legt ein zweistelliges Jahr ins 20. Jahrhundert: aus dem Jahr 26
  // würde 1926. Vier Ziffern stehen in der Datei, vier sollen es bleiben.
  if (jahr < 100) {
    const d = new Date(wanduhr)
    d.setUTCFullYear(jahr)
    wanduhr = d.getTime()
  }

  // Ein Datum, das es nicht gibt, rutscht in Date.UTC still in den Folgemonat.
  // Der Rückblick darauf ist die einzige Stelle, an der das auffällt.
  const probe = new Date(wanduhr)
  if (probe.getUTCMonth() !== monat - 1 || probe.getUTCDate() !== tag) return null

  return wanduhr - versatz * 60_000
}

/**
 * Ist das eine Koordinate, die es auf der Erde gibt?
 *
 * Genau 0/0 fällt bewusst mit heraus. Der Punkt liegt im Golf von Guinea, und
 * kein Aufzeichnungsgerät steht dort - so sieht die Voreinstellung eines
 * Geräts aus, das noch keinen Empfang hatte. Stehen liesse er eine Linie quer
 * über die Weltkarte.
 */
function usableCoord(lat: number, lon: number): boolean {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return false
  return lat !== 0 || lon !== 0
}

/** Der erste Halbsatz jeder Meldung über einen Abbruch. */
function abbruchSatz(t: GpxTruncation): string {
  return `Die Datei bricht bei ${String(t.readPercent)} % ab (${t.reason})`
}

/**
 * Was dem Nutzer über eine nur halb gelesene Datei zu sagen ist.
 *
 * Nicht die Zeichenposition und nicht die Zahl der verlorenen Elemente: mit
 * beidem kann im Auto niemand etwas anfangen. Was er braucht, sind drei Dinge -
 * dass es passiert ist, wie viel er trotzdem hat, und was er tun kann. Der
 * Prozentsatz beantwortet das zweite: bei 95 % fehlt die letzte halbe Stunde,
 * bei 12 % ist die Zahl darunter wertlos, und diesen Unterschied kann er sehen.
 */
function truncationWarning(t: GpxTruncation): string {
  return (
    `${abbruchSatz(t)}. Alles danach fehlt: Kilometer, Fahrzeit und Halte gehören nur zum ` +
    'gelesenen Anfang der Fahrt. Am besten die Aufzeichnung noch einmal exportieren.'
  )
}

/**
 * Grösste GPX-Datei, die eingelesen wird.
 *
 * Die Datei wird als Zeichenkette in den Speicher gelesen, und eine
 * Zeichenkette kostet im Browser das Doppelte ihrer Bytes. Ein Tag im
 * Sekundentakt sind gut 86 000 Punkte und damit knapp zehn Megabyte; fünfzig
 * lassen also mehrere Tage in einer Datei zu und halten gleichzeitig das
 * Telefon davon ab, beim Einlesen eines versehentlich gewählten Videos
 * stehenzubleiben.
 */
export const MAX_GPX_BYTES = 50 * 1024 * 1024

/**
 * Liest eine GPX-Datei.
 *
 * @throws GpxError wenn die Datei kein GPX ist oder keinen Track enthält.
 */
export function parseGpx(xml: string): GpxFile {
  if (typeof xml !== 'string' || !xml.includes('<')) {
    throw new GpxError('Das ist keine GPX-Datei.')
  }

  const points: GpxPoint[] = []
  let sawGpx = false
  let sawTrkpt = false
  let tracks = 0
  let segments = 0
  let dropped = 0

  /** Der Punkt, der gerade gelesen wird. */
  let current: GpxPoint | null = null
  /** Hat das laufende Segment schon einen brauchbaren Punkt geliefert? */
  let segmentZaehlt = false

  const abschliessen = () => {
    if (!current) return
    if (usableCoord(current.lat, current.lon)) {
      points.push(current)
      if (!segmentZaehlt) {
        segments++
        segmentZaehlt = true
      }
    } else {
      dropped++
    }
    current = null
  }

  const scan: XmlScan = { truncated: null }

  for (const tag of xmlTags(xml, scan)) {
    switch (tag.name) {
      case 'gpx':
        sawGpx = true
        break

      case 'trk':
        if (!tag.closing && !tag.selfClosing) tracks++
        break

      // Ein Wegpunkt oder ein Routenpunkt beendet einen offenen trkpt: seine
      // ele und seine time gehören ihm. Fehlen in einer Datei sowohl </trkpt>
      // als auch </trkseg>, liefen sie sonst in den Punkt davor hinein - und
      // eine Höhe von 8000 Metern am falschen Punkt sieht niemand.
      case 'wpt':
      case 'rte':
      case 'rtept':
        abschliessen()
        break

      case 'trkseg':
        // Ein offenes Element ohne Abschluss darf nicht in das nächste
        // Segment hineinlaufen.
        abschliessen()
        if (!tag.closing) segmentZaehlt = false
        break

      case 'trkpt':
        if (tag.closing) {
          abschliessen()
          break
        }
        // Ein trkpt im trkpt gibt es nicht; was offen ist, wird geschlossen.
        abschliessen()
        sawTrkpt = true
        current = {
          lat: numberAttr(tag.attrs, 'lat'),
          lon: numberAttr(tag.attrs, 'lon'),
          time: null,
          ele: null,
        }
        if (tag.selfClosing) abschliessen()
        break

      // Höhe und Zeit gelten nur innerhalb eines Punktes. Dieselben Namen
      // stehen in einer GPX-Datei auch an Wegpunkten und Routenpunkten, und
      // die gehören nicht zur Aufzeichnung.
      case 'ele':
        if (current && !tag.closing && !tag.selfClosing) current.ele = parseEle(textAfter(xml, tag))
        break

      case 'time':
        if (current && !tag.closing && !tag.selfClosing) {
          current.time = parseTime(textAfter(xml, tag))
        }
        break

      default:
        break
    }
  }
  abschliessen()

  if (!sawGpx && !sawTrkpt) throw new GpxError('Das ist keine GPX-Datei.')
  if (points.length === 0) {
    // Bricht die Datei ab, bevor überhaupt ein Punkt dastand, ist der Abbruch
    // die Erklärung und nicht ein Nebensatz dazu.
    throw new GpxError(
      scan.truncated
        ? `${abbruchSatz(scan.truncated)} und enthält davor keinen brauchbaren Trackpunkt.`
        : 'Die Datei enthält keine brauchbaren Trackpunkte.',
    )
  }

  return { points, tracks, segments, dropped, truncated: scan.truncated }
}

// --- Ableitungen ------------------------------------------------------------

/** Mittlerer Erdradius in Metern. */
const EARTH_M = 6_371_008.8

function toRad(deg: number): number {
  return (deg * Math.PI) / 180
}

/**
 * Abstand zweier Koordinaten in Metern, über die Kugel gerechnet.
 *
 * Haversine und nicht der Satz des Pythagoras auf Gradzahlen: ein Grad Länge
 * ist am Nordkap halb so lang wie in Zagreb, und eine Summe über zwanzigtausend
 * Punkte trägt jeden Fehler mit.
 */
export function haversineM(a: LatLon, b: LatLon): number {
  const dLat = toRad(b[0] - a[0])
  const dLon = toRad(b[1] - a[1])
  const lat1 = toRad(a[0])
  const lat2 = toRad(b[0])
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2
  return 2 * EARTH_M * Math.asin(Math.min(1, Math.sqrt(h)))
}

function abstand(a: GpxPoint, b: GpxPoint): number {
  return haversineM([a.lat, a.lon], [b.lat, b.lon])
}

/**
 * Umkreis, innerhalb dessen ein Punkt noch als "am selben Ort" gilt.
 *
 * Ein stehendes Gerät wandert im Empfang um einige Meter, in einer engen
 * Gasse oder unter Bäumen auch um mehr als zehn. Vierzig Meter fangen dieses
 * Rauschen ab, ohne eine Fahrt zu verschlucken: wer sich wirklich bewegt,
 * verlässt diesen Kreis in wenigen Sekunden - selbst im Stop-and-go legt ein
 * Fahrzeug in drei Minuten deutlich mehr zurück.
 */
export const STOP_RADIUS_M = 40

/**
 * Wie lange ein Fahrzeug stehen muss, damit es ein Halt ist.
 *
 * Drei Minuten. Darunter ist es Verkehr: eine lange Ampelphase dauert zwei
 * Minuten, ein Bahnübergang selten länger. Mit einer strengeren Schwelle
 * bestünde die Zeitleiste eines Stadtdurchgangs aus dreissig Einträgen und
 * wäre damit unbrauchbar - und die Standzeit zählte die halbe Innenstadt mit.
 */
export const STOP_MIN_SECONDS = 180

/** Ein Punkt, an dem ein Zeitstempel steht. */
type TimedPoint = GpxPoint & { time: number }

function timedPoints(points: GpxPoint[]): TimedPoint[] {
  return points.filter((p): p is TimedPoint => p.time !== null)
}

/** Eine Gradzahl zurück in das Fenster von -180 bis 180. */
function wrapLon(deg: number): number {
  return ((((deg + 180) % 360) + 360) % 360) - 180
}

/**
 * Der Mittelpunkt einer Punktwolke.
 *
 * Gemittelt wird nicht über die Gradzahlen selbst, sondern über ihren Abstand
 * zum ersten Punkt. Der Unterschied zeigt sich an der Datumsgrenze: 179,9 und
 * -179,9 liegen zwanzig Kilometer auseinander, ihr arithmetisches Mittel ist
 * aber 0 und damit der Golf von Guinea. Über die Abstände gerechnet kommt
 * dort +-180 heraus, wo der Halt auch wirklich war.
 *
 * Erlaubt ist diese Vereinfachung, weil ein Halt per Definition ein Umkreis von
 * wenigen Dutzend Metern ist (STOP_RADIUS_M): innerhalb dessen ist die Erde
 * flach genug, dass der Mittelwert der Abstände der richtige Ort ist. An den
 * Polen bliebe die Länge bedeutungslos - dort steht kein Camper, und ein Halt
 * dort wäre das kleinere der Probleme.
 */
function mittelpunkt(points: GpxPoint[]): LatLon {
  const erst = points[0]!
  let lat = 0
  let lon = 0
  for (const p of points) {
    lat += p.lat - erst.lat
    lon += wrapLon(p.lon - erst.lon)
  }
  return [erst.lat + lat / points.length, wrapLon(erst.lon + lon / points.length)]
}

/**
 * Findet die Haltepunkte: Folgen von Punkten, die im Umkreis eines Ankers
 * bleiben und dabei lange genug dauern.
 *
 * Über den Anker und nicht über die Geschwindigkeit zwischen zwei Punkten:
 * bei einer Aufzeichnung im Sekundentakt sind drei Meter Rauschen scheinbar
 * elf Stundenkilometer, und jede Standzeit sähe aus wie Schritttempo. Der
 * Umkreis dagegen bleibt vom Takt der Aufzeichnung unberührt.
 *
 * Punkte ohne Zeitstempel bleiben aussen vor - ohne Uhr gibt es keine Dauer.
 *
 * Der Durchlauf ist linear, und das ist keine Feinheit: der nächste Anker ist
 * immer der erste Punkt ausserhalb des betrachteten Umkreises, ob dieser
 * Umkreis nun ein Halt war oder nicht. Rückte der Anker bei einem Nichttreffer
 * nur um eins vor, liefe die innere Schleife jedes Mal von neuem bis zum Ende
 * des Umkreises - bei einer Datei mit stehengebliebener Uhr, in der alle
 * Punkte am selben Ort liegen und keiner davon je die Mindestdauer erreicht,
 * ist dieser Umkreis die ganze Datei. Aus 50 000 Punkten wurden so 21 Sekunden
 * und aus einem Mehrtagesexport Minuten, in denen die Bedienung stillsteht,
 * ohne dass jemand sieht warum. Logger mit hängender Uhr sind bei Dateien von
 * fremden Geräten der Normalfall und nicht der Sonderfall.
 *
 * Verloren geht dabei nichts von Belang. Alle Punkte eines übersprungenen
 * Umkreises liegen um denselben Anker; steht das Fahrzeug in Wahrheit schon
 * mitten darin, geht der Halt über den Umkreis hinaus und wird vom ersten
 * Punkt dahinter aus gefunden. Sein Beginn liegt dann höchstens um die Dauer
 * dieses Umkreises später - und die lag unter der Mindestdauer, sonst wäre er
 * selbst schon ein Halt gewesen.
 */
export function findStops(
  points: GpxPoint[],
  radiusM = STOP_RADIUS_M,
  minSeconds = STOP_MIN_SECONDS,
): TrackStop[] {
  const timed = timedPoints(points)
  const out: TrackStop[] = []

  let i = 0
  while (i < timed.length) {
    const anker = timed[i]!
    let j = i
    while (j + 1 < timed.length && abstand(anker, timed[j + 1]!) <= radiusM) j++

    const bis = timed[j]!
    const sekunden = (bis.time - anker.time) / 1000
    if (j > i && sekunden >= minSeconds) {
      out.push({ from: anker.time, to: bis.time, at: mittelpunkt(timed.slice(i, j + 1)) })
    }
    // In beiden Fällen weiter beim ersten Punkt ausserhalb des Umkreises. Bei
    // j === i ist das der nächste Punkt, der Fortschritt ist also immer
    // mindestens eins, und jeder Punkt wird von genau einem Anker angesehen.
    i = j + 1
  }

  return out
}

/**
 * Umkreis, den ein Punkt verlassen haben muss, damit die Strecke bis dorthin
 * zählt. Fünfzehn Meter.
 *
 * Warum es diese Schranke überhaupt braucht: ein stehendes Gerät wandert im
 * Empfang, und die reine Summe der Abstände macht aus diesem Wandern
 * Kilometer. Bei zwei Metern Streuung und sekündlicher Aufzeichnung sind das
 * 7,5 km je Stunde - eine Nacht auf dem Stellplatz erfindet so mehr Strecke,
 * als die Etappe lang war, und diese Zahl geht auf Knopfdruck als "tatsächlich
 * gefahren" ins Tagebuch.
 *
 * Warum ein Umkreis um den letzten gezählten Punkt und nicht eine Schranke je
 * Schritt: eine Schranke je Schritt wirft die Strecke weg, der Umkreis stellt
 * sie nur zurück. Wer im Schritttempo durch eine Altstadt fährt, legt zwischen
 * zwei Fixes zwei Meter zurück; jeder einzelne Schritt bleibt damit unter jeder
 * Schranke, die das Rauschen abfängt, und die Altstadt käme gar nicht in die
 * Summe. Mit dem Umkreis wird derselbe Weg nach sieben Sekunden am Stück
 * gezählt, wenn der Wagen den Kreis verlässt - nachgemessen bleibt das unter
 * einem Prozent, während eine Schranke von drei Metern je Schritt von
 * derselben Fahrt 15,6 Prozent frisst und trotzdem noch 20 km Rauschen
 * durchlässt.
 *
 * Warum kein Tempo zwischen zwei Punkten: die Aufzeichnungsrate ist nicht
 * bekannt. Drei Meter Rauschen sind im Sekundentakt scheinbar elf
 * Stundenkilometer und bei einem Punkt alle dreissig Sekunden ein Drittel
 * davon - dieselbe Schwelle täte also einmal zu viel und einmal zu wenig.
 * Dieselbe Überlegung steht schon über findStops. Dazu kommt: ohne
 * Zeitstempel gibt es kein Tempo, und eine Datei ohne Uhr hätte dann gar keine
 * Kilometer mehr.
 *
 * Warum fünfzehn Meter und nicht fünf oder vierzig: nach oben begrenzt die
 * Krümmung. Gezählt wird die Sehne zwischen zwei Ankern, und eine Sehne von
 * fünfzehn Metern schneidet Kurven ab - bei einem Kurvenradius von 50 Metern
 * um 0,4 Prozent, in einer Kehre von zehn Metern um 12 Prozent. Kehren und
 * Kreisel sind aber kurz: auf einer Passstrasse mit einer Kehre alle
 * fünfhundert Meter bleiben davon 0,3 Prozent der Tagesstrecke übrig. Eine
 * harte Grenze steht daneben: wird der Umkreis so gross wie der Durchmesser
 * der engsten gefahrenen Kurve, verlässt ihn ein Fahrzeug in dieser Kurve nie
 * mehr - im Kreisverkehr bliebe der Anker dann in der Mitte liegen. Zwanzig
 * Meter wären für eine Kehre von zehn genau dieser Fall, fünfzehn nicht.
 *
 * Nach unten begrenzt das Rauschen selbst, und zwar stärker. Es ist nicht
 * beschränkt: einzelne Fixes liegen weiter daneben als der Rest, und jeder,
 * der über den Umkreis hinausreicht, wird zweimal gezählt - hin und zurück.
 * Von acht Stunden Stand mit drei Metern gaussscher Streuung bleiben mit zehn
 * Metern noch 21 km stehen, mit zwölf 6 km, mit fünfzehn 2 km. Fünfzehn ist
 * der Punkt, an dem beide Seiten unter einem Prozent liegen.
 */
export const MOVE_RADIUS_M = 15

/**
 * Zählt die Strecke Punkt für Punkt und liefert nach jedem Punkt den Stand
 * in Metern.
 *
 * Zwei Dinge werden nicht gezählt, und sie fangen zwei verschiedene Fälle ab.
 *
 * Das eine ist der erkannte Halt: was zwischen Ankunft und Abfahrt an
 * Koordinaten hereinkommt, ist Rauschen und nicht Fahrt, ganz gleich wie weit
 * es wandert. Der Anker bleibt darum auf dem Punkt der Ankunft stehen, und
 * gezählt wird erst wieder die Sehne von dort zur Abfahrt. Über den Halt
 * hinweg gehen dabei höchstens die vierzig Meter verloren, die ein Halt
 * überhaupt gross sein darf (STOP_RADIUS_M).
 *
 * Das andere ist der Umkreis (MOVE_RADIUS_M), und er ist der wichtigere: die
 * Halterkennung kennt erst drei Minuten am selben Ort (STOP_MIN_SECONDS).
 * Ampeln, Stau und Rangieren liegen darunter, und ein Tag Stadtverkehr mit
 * hundert Ampelphasen von je anderthalb Minuten hat davon kein einziges
 * gefunden - aus 20 gefahrenen Kilometern wurden 39. Ohne Zeitstempel findet
 * sie überhaupt nichts, und genau dann steht der Umkreis allein da.
 *
 * Der Durchlauf ist linear: je Punkt eine Abstandsrechnung, und der Zeiger auf
 * den nächsten Halt läuft über die ganze Datei nur einmal vorwärts. Läuft
 * die Uhr in der Datei rückwärts, findet dieser Zeiger die späteren Halte
 * nicht mehr - dann trägt der Umkreis den Fall allein, und das ist die
 * richtige Reihenfolge: lieber ein paar Meter Rauschen zu viel als eine
 * Rechnung, die bei einer kaputten Uhr quadratisch wird.
 */
function streckenmesser(stops: TrackStop[]): (p: GpxPoint) => number {
  let anker: GpxPoint | null = null
  /** In welchem Halt der Anker liegt, oder -1. */
  let ankerHalt = -1
  let strecke = 0
  let naechster = 0

  /** Der Halt, in dem dieser Punkt liegt, als Nummer - oder -1 für keinen. */
  const haltVon = (p: GpxPoint): number => {
    if (p.time === null) return -1
    while (naechster < stops.length && stops[naechster]!.to < p.time) naechster++
    const halt = stops[naechster]
    return halt && p.time >= halt.from ? naechster : -1
  }

  return (p) => {
    const halt = haltVon(p)
    if (anker === null) {
      anker = p
      ankerHalt = halt
      return 0
    }
    // Derselbe Halt wie der Anker: das ist Stand, kein Weg.
    if (halt >= 0 && halt === ankerHalt) return strecke

    const weg = abstand(anker, p)
    if (weg >= MOVE_RADIUS_M) {
      strecke += weg
      anker = p
      ankerHalt = halt
    }
    return strecke
  }
}

/**
 * Die tatsächlich gefahrene Strecke in Metern.
 *
 * Gezählt wird die Sehne von einem gezählten Punkt zum nächsten, der den
 * Umkreis von MOVE_RADIUS_M verlassen hat, und innerhalb eines erkannten Halts
 * gar nichts. Was dabei herauskommt, ist mehr als die Luftlinie und meistens
 * auch mehr als die berechnete Strasse - jede Kurve, jede Umfahrung und jede
 * Suche nach dem Stellplatz steht darin. Was nicht darin steht, ist das
 * Wandern des Empfangs im Stand; die schlichte Summe aller Abstände zählte
 * das mit und kam damit bei acht Stunden Stellplatz auf bis zu 90 Kilometer,
 * die nie gefahren wurden.
 *
 * @param stops Die Halte, wenn sie schon berechnet sind. Sonst werden sie hier
 *   berechnet - deriveTrack hat sie ohnehin und reicht sie durch, damit die
 *   Datei nicht zweimal durchlaufen wird.
 */
export function trackDistanceM(points: GpxPoint[], stops: TrackStop[] = findStops(points)): number {
  const messen = streckenmesser(stops)
  let strecke = 0
  for (const p of points) strecke = messen(p)
  return strecke
}

/**
 * Fenster des gleitenden Mittelwerts über die Höhen.
 *
 * Fünf Messwerte. Bei der üblichen Aufzeichnung im Sekundentakt ist das eine
 * Glättung über fünf Sekunden: kurz genug, dass eine Passauffahrt ihre Form
 * behält, lang genug, dass das Rauschen einzelner Messungen sich aufhebt.
 */
export const ELEVATION_WINDOW = 5

/**
 * Ab welchem Anstieg gezählt wird, in Metern.
 *
 * Die Höhe aus einem GPS-Empfänger schwankt um deutlich mehr als die
 * Koordinate, gerne um fünf bis fünfzehn Meter, und sie schwankt nicht von
 * Messung zu Messung, sondern wandert über Minuten. Genau deshalb reicht die
 * Glättung allein nicht: ein gleitender Mittelwert über fünf Werte nimmt das
 * Zittern heraus, nicht das Wandern. Ohne Schwelle summierte sich dieses
 * Wandern auf einer Fahrt durch die Poebene zu hunderten Metern Anstieg.
 *
 * Zehn Meter sind die Höhe eines dreistöckigen Hauses: darunter gibt es auf
 * einer Autofahrt nichts zu zählen, darüber ist jeder Anstieg echt. Verloren
 * geht dabei nichts Nennenswertes - der Bezugspunkt zieht in Schritten nach,
 * eine Passauffahrt von 800 Metern wird also vollständig gezählt und nicht
 * etwa in Zehnerschritten beschnitten. Nur der letzte angefangene Schritt vor
 * dem Gipfel bleibt liegen.
 *
 * Dieselbe Schwelle gilt nach unten, bevor ein neuer Tiefpunkt als Bezug gilt.
 * Ohne das wäre die Schwelle wirkungslos: das Rauschen setzte den Bezug bei
 * jedem Tiefpunkt neu, und der nächste Ausschlag nach oben zählte doch.
 */
export const ELEVATION_THRESHOLD_M = 10

/** Gleitender Mittelwert. Am Rand wird das Fenster verkürzt, nicht verlängert. */
export function smoothElevation(values: number[], window = ELEVATION_WINDOW): number[] {
  if (values.length === 0) return []
  const half = Math.max(0, Math.floor(window / 2))
  if (half === 0) return [...values]

  return values.map((_, i) => {
    const von = Math.max(0, i - half)
    const bis = Math.min(values.length - 1, i + half)
    let sum = 0
    for (let k = von; k <= bis; k++) sum += values[k]!
    return sum / (bis - von + 1)
  })
}

/**
 * Summe des Anstiegs mit Hysterese.
 *
 * Gezählt wird gegen einen mitlaufenden Bezugspunkt, und der bewegt sich nur,
 * wenn die Schwelle überschritten ist - nach oben zählend, nach unten still.
 * Ein Auf und Ab innerhalb der Schwelle bewegt nichts, ein echter Anstieg
 * dagegen vollständig.
 */
export function climbM(values: number[], threshold = ELEVATION_THRESHOLD_M): number {
  if (values.length === 0) return 0
  let bezug = values[0]!
  let gain = 0
  for (const value of values) {
    if (value - bezug >= threshold) {
      gain += value - bezug
      bezug = value
    } else if (bezug - value >= threshold) {
      bezug = value
    }
  }
  return gain
}

/**
 * Wie viele Punkte das gespeicherte Höhenprofil höchstens hat.
 *
 * Sechzig Punkte auf einem Diagramm von wenigen Zentimetern Breite sind
 * reichlich - vorausgesetzt, sie sitzen richtig. Ausgewählt wird deshalb nach
 * Höhe (thinGeometry mit 'profile'): der Gipfel des Passes bleibt, die
 * zwanzigste Messung einer gleichmässigen Steigung fällt weg. Gleichmässig
 * gewählt konnte ein Profil den Pass um bis zu einen halben Abstand verfehlen
 * und einen Berg zeigen, der niedriger war als der gefahrene.
 */
export const MAX_PROFILE_POINTS = 60

/**
 * Das Höhenprofil einer Aufzeichnung.
 *
 * Aufgetragen wird über die gefahrene Strecke und nicht über die Zeit: eine
 * Passauffahrt soll als Berg dastehen und nicht als Plateau, nur weil dort
 * langsam gefahren wurde.
 *
 * Über dieselbe Strecke wie trackDistanceM, mit demselben Zähler: die Achse
 * unter dem Profil und die Zahl im Tagebuch müssen dasselbe meinen. Sonst
 * stünde unter einem Profil, das bis 90 km reicht, eine Etappe von 200 Metern
 * - und die 90 km wären die Nacht auf dem Stellplatz.
 *
 * @param stops Wie bei trackDistanceM: durchgereicht, wenn schon berechnet.
 * @returns null, wenn die Datei keine oder zu wenige Höhen mitbringt.
 */
export function elevationOf(
  points: GpxPoint[],
  stops: TrackStop[] = findStops(points),
): TrackElevation | null {
  const km: number[] = []
  const roh: number[] = []

  const messen = streckenmesser(stops)
  for (const point of points) {
    const strecke = messen(point)
    if (point.ele === null) continue
    km.push(strecke / 1000)
    roh.push(point.ele)
  }
  if (roh.length < 2) return null

  // Geglättet wird vor allem anderen: Höchst- und Tiefstwert einer einzelnen
  // Messung wären sonst der Ausreisser und nicht der Pass.
  const geglaettet = smoothElevation(roh)
  const profil: TrackProfilePoint[] = geglaettet.map((ele, i) => [
    Math.round(km[i]! * 100) / 100,
    Math.round(ele * 10) / 10,
  ])

  // Schleife statt Math.min(...geglaettet): eine Aufzeichnung im Sekundentakt
  // hat nach einem langen Tag sechsstellig viele Punkte, und so viele Argumente
  // nimmt kein Funktionsaufruf entgegen - das endete in einem Stapelüberlauf
  // ausgerechnet bei der längsten Etappe.
  let min = geglaettet[0]!
  let max = geglaettet[0]!
  for (const ele of geglaettet) {
    if (ele < min) min = ele
    if (ele > max) max = ele
  }

  return {
    profile: thinGeometry(profil, MAX_PROFILE_POINTS, 'profile'),
    min: Math.round(min),
    max: Math.round(max),
    gain: Math.round(climbM(geglaettet)),
  }
}

/**
 * Wie viele Punkte des Wegs gespeichert werden.
 *
 * Der Track liegt im Tagebucheintrag, und der geht bei jeder Änderung als
 * Ganzes durch den Abgleich - beim Tippen also alle paar Sekunden. Eine
 * Aufzeichnung im Sekundentakt hat nach sechs Stunden gut zwanzigtausend
 * Punkte, das ist über ein Megabyte JSON je Etappe und je Übertragung.
 *
 * Dreihundert sind dieselbe Grössenordnung wie bei der berechneten Strasse
 * (MAX_GEOMETRY_POINTS in route.ts, 400 für eine ganze Etappe) - mehr trägt
 * eine Karte nicht, die die ganze Etappe zeigt. Wo die dreihundert liegen,
 * entscheidet thinGeometry nach Bedeutung und nicht nach Abstand: auf der
 * geraden Anfahrt braucht es kaum welche, in der Passkehre viele.
 *
 * Entscheidend ist die Reihenfolge: gerechnet wird über alle Rohpunkte, erst
 * das Ergebnis wird ausgedünnt. Die Kilometer, die Stopps und der Anstieg
 * verlieren durch das Ausdünnen also nichts.
 */
export const MAX_TRACK_POINTS = 300

/**
 * Wie viele Stopps in die Zeitleiste kommen.
 *
 * Auch über der Schwelle kann ein Tag im Stadtverkehr viele Halte haben. Steht
 * mehr an, fallen die kürzesten heraus: die Liste soll den Tag erzählen, und
 * dafür zählen die langen Halte.
 */
export const MAX_STOPS = 40

function kuerzeStopps(stops: TrackStop[]): TrackStop[] {
  if (stops.length <= MAX_STOPS) return stops
  return [...stops]
    .sort((a, b) => b.to - b.from - (a.to - a.from))
    .slice(0, MAX_STOPS)
    .sort((a, b) => a.from - b.from)
}

/**
 * Wie viele Punkte mit Uhrzeit im Track liegen bleiben.
 *
 * Zwölf. Sie sind die Grundlage des Wettervorschlags, und der Dienst liefert
 * stündlich - mehr als ein Punkt je Stunde ist also eine Genauigkeit, die es in
 * der Antwort gar nicht gibt. Eine Tagesetappe dauert selten länger als zwölf
 * Stunden; wird sie es doch, rücken die Punkte auseinander und der Verlauf
 * wird gröber, aber er bleibt einer. Dieselbe Zahl steht als MaxSamples im
 * Server (internal/weather), und aus demselben Grund.
 */
export const MAX_TRACK_FIXES = 12

/**
 * Wie weit ein Zeitstempel vor dem Import liegen darf, um noch als Uhrzeit
 * dieser Fahrt zu gelten. Fünf Jahre.
 *
 * Der Anlass ist ein Fehler, den ältere Empfänger bis heute machen: der
 * GPS-Wochenzähler läuft über, und das Gerät schreibt eine Zeit, die genau
 * 1024 Wochen zurückliegt - aus 2026 wird 2006. Die Datei sieht danach
 * tadellos aus. Jede Zahl darin ist eine Zahl, jeder Zeitstempel ist ein
 * gültiger Zeitstempel, und die Reihenfolge stimmt auch.
 *
 * Warum fünf Jahre die richtige Grenze sind: der Versatz eines Überlaufs ist
 * ein Vielfaches von 1024 Wochen, also mindestens 19,6 Jahre. Alles zwischen
 * "älter als jede Aufzeichnung, die zu einer Reise gehört, die man gerade
 * einträgt" und diesen 19,6 Jahren tut es; fünf Jahre liegen bequem in der
 * Mitte und lassen auch die Datei durch, die jemand ein Jahr später nachträgt.
 *
 * Und warum die Grenze überhaupt so grosszügig sein darf: was an ihr scheitert,
 * kostet nur den Wettervorschlag. Kilometer, Fahrzeit und Halte bleiben
 * richtig, denn ein Überlauf verschiebt alle Zeitstempel um denselben Betrag,
 * und Differenzen bleiben davon unberührt. Falsch wird allein die absolute
 * Zeit - und genau die ist es, mit der das Wetterarchiv befragt würde.
 */
export const ZEIT_RUECKBLICK_MS = 5 * 365 * 24 * 3_600_000

/**
 * Taugt dieser Zeitstempel als Zeitpunkt der Fahrt?
 *
 * Nur nach hinten geprüft und nicht nach vorn, und das ist Absicht. Ein Gerät,
 * das der Uhr um Minuten vorausläuft, ist der Normalfall; ein Buch, das vor der
 * Abfahrt gesetzt wird, trägt ohnehin lauter Daten in der Zukunft; und der
 * Wetterdienst weist eine Anfrage nach der Zukunft von sich aus ab, mit einer
 * Meldung, die man lesen kann (siehe futureTolerance in internal/weather).
 * Nach hinten gibt es diese zweite Schranke nicht: das Archiv reicht bis 1940
 * zurück und beantwortet die Frage nach 2006 bereitwillig.
 */
export function plausibleZeit(zeit: number, jetzt: number): boolean {
  if (!Number.isFinite(zeit)) return false
  return zeit >= jetzt - ZEIT_RUECKBLICK_MS
}

/** Die volle Stunde, in der ein Zeitpunkt liegt. */
function stundeVon(ms: number): number {
  return Math.floor(ms / 3_600_000)
}

/**
 * Die Punkte mit Uhrzeit, die im Track liegen bleiben: einer je Stunde.
 *
 * Der erste Punkt jeder angebrochenen Stunde, echt und nicht gerechnet - siehe
 * TrackFix in types.ts. Sind es mehr als MAX_TRACK_FIXES Stunden, wird
 * gleichmässig ausgedünnt; Anfang und Ende bleiben dabei immer stehen, denn sie
 * sind der Rahmen der Fahrt.
 *
 * Gleichmässig und nicht nach Bedeutung wie bei der Geometrie: bedeutend ist
 * hier die Zeit, und die vergeht gleichmässig. Eine Kurve, die für den
 * Streckenverlauf viel bedeutet, sagt über das Wetter nichts.
 *
 * Punkte ohne Zeitstempel bleiben aussen vor - ohne Uhr weiss niemand, wann
 * die Fahrt war, und dann gibt es auch keinen Wettervorschlag.
 *
 * Punkte mit einer unglaubwürdigen Uhrzeit ebenso, und aus demselben Grund
 * (siehe plausibleZeit). Eine falsch gestellte Uhr ist schlimmer als eine
 * fehlende: der Dienst antwortet auf die Frage nach dem 17. August 2006 mit
 * echten Messwerten, und daraus wird ein Satz über einen Tag, an dem niemand
 * unterwegs war. Was hier herausfällt, sagt der Import dem Nutzer - siehe
 * zeitWarnung.
 *
 * @param jetzt Bezugspunkt für die Glaubwürdigkeit, üblicherweise der
 *   Zeitpunkt des Imports.
 */
export function trackFixes(
  points: GpxPoint[],
  max = MAX_TRACK_FIXES,
  jetzt = Date.now(),
): TrackFix[] {
  const timed = timedPoints(points)
  if (timed.length === 0) return []

  const proStunde: TrackFix[] = []
  let letzte: number | null = null
  for (const p of timed) {
    if (!plausibleZeit(p.time, jetzt)) continue
    const stunde = stundeVon(p.time)
    if (stunde === letzte) continue
    letzte = stunde
    proStunde.push({ at: [p.lat, p.lon], t: p.time })
  }
  return duenneFixes(proStunde, max)
}

/**
 * Was dem Nutzer über eine Aufzeichnung mit falsch gestellter Uhr zu sagen ist.
 *
 * Der Satz nennt beides: was noch stimmt und was nicht. Eine Warnung, die nur
 * "kaputte Uhr" sagt, liesse offen, ob die Datei überhaupt zu gebrauchen ist -
 * und sie ist zu gebrauchen, denn Kilometer, Fahrzeit und Halte sind
 * Zeitdifferenzen und von einem gleichmässigen Versatz unberührt.
 *
 * @returns null, wenn die Zeitstempel in Ordnung sind.
 */
function zeitWarnung(points: GpxPoint[], jetzt: number): string | null {
  const timed = timedPoints(points)
  if (timed.length === 0) return null
  const fruehester = timed[0]!.time
  if (plausibleZeit(fruehester, jetzt)) return null

  const jahr = new Date(fruehester).getUTCFullYear()
  return (
    `Die Aufzeichnung ist auf ${String(jahr)} datiert und damit weit vor den Tag des Imports - ` +
    'die Uhr des Geräts war beim Aufzeichnen nicht gestellt. Kilometer, Fahrzeit und Halte ' +
    'stimmen trotzdem, sie sind Zeitdifferenzen; Datum und Uhrzeit stimmen nicht, und Wetter ' +
    'wird für diesen Tag nicht geholt.'
  )
}

/** Die Sätze einer Lese zu einer Warnung, oder null, wenn nichts zu sagen ist. */
function warnungAus(saetze: (string | null)[]): string | null {
  const echte = saetze.filter((satz): satz is string => satz !== null)
  return echte.length > 0 ? echte.join(' ') : null
}

/**
 * Dünnt eine Liste von Zeitpunkten gleichmässig auf höchstens max aus.
 *
 * Der erste und der letzte bleiben immer: sie sind Anfang und Ende der Fahrt,
 * und ohne sie stünde der Vorschlag über einen anderen Zeitraum als den
 * gefahrenen.
 */
function duenneFixes(fixes: TrackFix[], max: number): TrackFix[] {
  if (max < 1) return []
  if (fixes.length <= max) return fixes
  if (max === 1) return [fixes[0]!]

  const out: TrackFix[] = []
  const letzter = fixes.length - 1
  for (let i = 0; i < max; i++) {
    const platz = Math.round((i * letzter) / (max - 1))
    const fix = fixes[platz]!
    // Bei sehr kurzen Listen kann derselbe Platz zweimal getroffen werden.
    if (out.length === 0 || out[out.length - 1]!.t !== fix.t) out.push(fix)
  }
  return out
}

/** Was beim Ableiten sonst noch bekannt ist und im Track stehen bleibt. */
export interface TrackHerkunft {
  /** Name der Datei, damit später erkennbar ist, was importiert wurde. */
  fileName: string
  /** Zeitpunkt des Imports, Millisekunden seit 1970. */
  importedAt: number
  /** Zahl der trkseg, die Punkte geliefert haben. */
  segments?: number
  /** Punkte, die beim Lesen übersprungen wurden. */
  dropped?: number
}

/**
 * Macht aus den Rohpunkten das, was im Tagebuch liegen bleibt.
 *
 * Alle Zahlen entstehen aus den Rohpunkten, gespeichert wird die ausgedünnte
 * Fassung. Gerechnet wird also einmal beim Import und nie wieder - die Anzeige
 * liest nur noch ab.
 */
export function deriveTrack(points: GpxPoint[], herkunft: TrackHerkunft): DiaryTrack {
  const timed = timedPoints(points)
  const start = timed[0]?.time ?? null
  const ende = timed.length > 0 ? timed[timed.length - 1]!.time : null

  const stops = findStops(points)
  const stoppedSeconds = Math.round(
    stops.reduce((sum, stop) => sum + (stop.to - stop.from) / 1000, 0),
  )
  const totalSeconds =
    start !== null && ende !== null ? Math.max(0, Math.round((ende - start) / 1000)) : 0

  const elevation = elevationOf(points, stops)
  // Gemessen wird die Glaubwürdigkeit der Uhrzeiten am Zeitpunkt des Imports
  // und nicht an der Uhr dieses Augenblicks: derselbe Bestand soll morgen
  // dasselbe ergeben wie heute.
  const fixes = trackFixes(points, MAX_TRACK_FIXES, herkunft.importedAt)

  return {
    geometry: thinGeometry(
      points.map((p): LatLon => [p.lat, p.lon]),
      MAX_TRACK_POINTS,
    ),
    distanceKm: Math.round(trackDistanceM(points, stops)) / 1000,
    startedAt: start,
    endedAt: ende,
    totalSeconds,
    // Die Fahrzeit ist die Gesamtzeit ohne die Halte. Sie einzeln zu summieren
    // wäre dasselbe Ergebnis mit einem zweiten Rundungsfehler.
    movingSeconds: Math.max(0, totalSeconds - stoppedSeconds),
    stoppedSeconds,
    stops: kuerzeStopps(stops),
    ...(fixes.length > 0 ? { fixes } : {}),
    ...(elevation ? { elevation } : {}),
    pointCount: points.length,
    segments: herkunft.segments ?? 1,
    dropped: herkunft.dropped ?? 0,
    fileName: herkunft.fileName,
    importedAt: herkunft.importedAt,
    fingerprint: fingerprintOf(points),
  }
}

// --- Mehrere Dateien auf einer Etappe ----------------------------------------

/**
 * Fingerabdruck der Rohpunkte einer Aufzeichnung.
 *
 * Woran erkennt man, dass dieselbe Fahrt zum zweiten Mal hereinkommt? Der
 * Dateiname taugt nicht: das Gerät vergibt seine Namen nach Datum und Nummer
 * und fängt wieder von vorne an, zwei verschiedene Fahrten heissen also
 * irgendwann gleich. Die Zahl der Punkte taugt auch nicht: der Columbus
 * zeichnet wahlweise nach Zeit, nach Frequenz oder nach Distanz auf, dieselbe
 * Fahrt hat also je nach Einstellung ganz verschiedene Punktzahlen.
 *
 * Bleibt der Inhalt. Gemischt werden Ort (auf etwa einen Meter gerundet) und
 * Zeit jedes Rohpunktes, in Dateireihenfolge. Zwei Importe derselben Datei
 * ergeben denselben Abdruck, zwei verschiedene Fahrten nicht.
 *
 * Der Abdruck ist die schärfere, aber nicht die wichtigere Prüfung: er
 * erkennt genau dieselbe Datei und sonst nichts. Ein Gerät, das nachmittags
 * noch einmal den ganzen Tag ausgibt, liefert eine Datei, die die erste
 * enthält und trotzdem anders aussieht - dagegen hilft nur der Zeitbereich,
 * siehe trackKonflikt. Der Abdruck sagt dem Nutzer, welcher der beiden Fälle
 * vorliegt, und das ist der Unterschied zwischen "hast du schon" und "das
 * überschneidet sich".
 *
 * Nicht kryptografisch und nicht darauf angewiesen: hier geht es um ein paar
 * Dutzend Dateien auf einem Telefon, nicht um Angreifer. Ein Zusammenstoss
 * kostet einen falschen Satz in einer Warnung, keine Daten - geschrieben wird
 * ohnehin nichts ohne Knopfdruck.
 */
export function fingerprintOf(points: GpxPoint[]): string {
  // FNV-1a, 32 Bit. Über Zahlen statt über Zeichenketten: eine Aufzeichnung
  // hat sechsstellig viele Punkte, und für jeden eine Zeichenkette zu bauen
  // wäre die teuerste Art, dasselbe Ergebnis zu bekommen.
  let hash = 0x811c9dc5
  const mische = (wert: number) => {
    hash = Math.imul(hash ^ (wert | 0), 0x01000193)
  }

  for (const p of points) {
    mische(Math.round(p.lat * 100_000))
    mische(Math.round(p.lon * 100_000))
    // Zeitstempel sind grösser als 32 Bit; beide Hälften gehen ein, sonst
    // wären zwei Punkte im Abstand von 49 Tagen ununterscheidbar.
    const zeit = p.time ?? -1
    mische(zeit % 4_294_967_296)
    mische(Math.floor(zeit / 4_294_967_296))
  }

  return `${points.length.toString(36)}-${(hash >>> 0).toString(36)}`
}

/**
 * Legt die Zeitpunkte mehrerer Teile zu einer Liste zusammen.
 *
 * Nach der Uhr sortiert, höchstens einer je Stunde, und am Ende auf
 * MAX_TRACK_FIXES ausgedünnt - dieselbe Regel wie beim Import einer einzelnen
 * Datei, also dasselbe Ergebnis. Zwei Teile, die sich überschneiden, ergeben so
 * keine Stunde zweimal: der Dienst hätte für sie ohnehin nur einen Wert, und
 * eine doppelte Stunde machte den Verlauf länger, als der Tag war.
 */
function legeFixesZusammen(fixes: TrackFix[]): TrackFix[] {
  const sortiert = [...fixes].sort((a, b) => a.t - b.t)
  const proStunde: TrackFix[] = []
  let letzte: number | null = null
  for (const fix of sortiert) {
    const stunde = stundeVon(fix.t)
    if (stunde === letzte) continue
    letzte = stunde
    proStunde.push(fix)
  }
  return duenneFixes(proStunde, MAX_TRACK_FIXES)
}

/** Ein Track aus einer einzelnen Datei als Teil. */
function alsTeil(track: DiaryTrack): TrackPart {
  return {
    geometry: track.geometry,
    distanceKm: track.distanceKm,
    startedAt: track.startedAt,
    endedAt: track.endedAt,
    totalSeconds: track.totalSeconds,
    movingSeconds: track.movingSeconds,
    stoppedSeconds: track.stoppedSeconds,
    // Nur bei einem Track, der selbst schon aus mehreren Dateien besteht - der
    // wird hier normalerweise nicht zum Teil gemacht (trackParts gibt dann
    // seine eigenen Teile zurück). Mitgenommen wird es trotzdem, damit die
    // Zeiten eines Teils zusammenpassen, wo es doch vorkommt.
    ...(track.unrecordedSeconds ? { unrecordedSeconds: track.unrecordedSeconds } : {}),
    stops: track.stops,
    ...(track.fixes && track.fixes.length > 0 ? { fixes: track.fixes } : {}),
    ...(track.elevation ? { elevation: track.elevation } : {}),
    pointCount: track.pointCount,
    segments: track.segments,
    dropped: track.dropped,
    fileName: track.fileName,
    importedAt: track.importedAt,
    fingerprint: track.fingerprint ?? '',
  }
}

/**
 * Die Teile, aus denen ein Track besteht - auch wenn es nur einer ist.
 *
 * Damit muss ausserhalb niemand zwei Fälle unterscheiden: ein Track aus einer
 * Datei ist eine Liste mit einem Teil, und alles, was Teile zusammenlegt oder
 * herausnimmt, arbeitet immer auf derselben Liste.
 */
export function trackParts(track: DiaryTrack): TrackPart[] {
  return track.parts && track.parts.length > 0 ? track.parts : [alsTeil(track)]
}

/** Ein einzelner Teil zurück als vollständiger Track. */
function alsTrack(teil: TrackPart): DiaryTrack {
  const track: DiaryTrack = { ...teil }
  // Ein leerer Abdruck ist keiner: er kommt aus einer Datei, die älter ist als
  // das Feld, und soll auch danach nicht so aussehen, als stünde einer da.
  if (track.fingerprint === '') delete track.fingerprint
  return track
}

/** Was von einer Aufzeichnung gebraucht wird, um sie einzuordnen. */
interface Zeitlich {
  startedAt: number | null
  importedAt: number
}

/**
 * Reihenfolge der Teile: nach der Uhr, nicht nach dem Import.
 *
 * Wer den Nachmittag vor dem Vormittag einliest, bekommt trotzdem den Tag in
 * der Reihenfolge, in der er gefahren wurde. Teile ohne Uhr haben keine Stelle
 * in der Zeit und hängen sich hinten an, in der Reihenfolge ihres Imports.
 */
function vergleicheTeile(a: Zeitlich, b: Zeitlich): number {
  const az = a.startedAt ?? Number.MAX_SAFE_INTEGER
  const bz = b.startedAt ?? Number.MAX_SAFE_INTEGER
  return az !== bz ? az - bz : a.importedAt - b.importedAt
}

/**
 * Aufzeichnungen in der Reihenfolge, in der sie gefahren wurden.
 *
 * Die eine Stelle, an der diese Reihenfolge festgelegt ist. Wer mehrere
 * Dateien auf einmal wählt, bekommt sie hier sortiert - und zwar nach dem
 * ersten Zeitstempel in der Datei und ausdrücklich nicht nach ihrem Namen: das
 * Garmin nennt seine Dateien nach der eigenen Epoche (1154749831.gpx), der
 * Columbus nach Datum und laufender Nummer, und ein drittes Gerät nennt sie
 * wieder anders. Auch nicht nach der Reihenfolge, in der das Betriebssystem
 * sie in den Dialog gelegt hat: die ist alphabetisch und damit dieselbe
 * Auskunft wie der Name.
 *
 * Fehlt der Zeitstempel, gibt es nichts zu sortieren. Solche Aufzeichnungen
 * hängen sich hinten an, in der Reihenfolge, in der sie gewählt wurden -
 * dieselbe Regel, nach der mergeTrackParts sie einordnet, und aus demselben
 * Grund: ohne Uhr haben sie keine Stelle in der Zeit. Die Sortierung ist
 * stabil, ihre Reihenfolge untereinander bleibt also die der Auswahl.
 *
 * @returns Eine neue Liste. Die übergebene bleibt unberührt.
 */
export function nachFahrtzeit<T extends Zeitlich>(teile: T[]): T[] {
  return [...teile].sort(vergleicheTeile)
}

/** Was zwischen zwei aufeinanderfolgenden Teilen liegt. */
interface Bruecke {
  /** Strecke zwischen dem Ende des einen und dem Anfang des nächsten Teils. */
  meter: number
  /** Der Halt, wenn das Fahrzeug in der Lücke gestanden hat. */
  stop: TrackStop | null
  /** Sekunden, über die keine der beiden Dateien etwas sagt. */
  nichtAufgezeichnet: number
}

/**
 * Die Lücke zwischen zwei Teilen, gelesen wie jede andere Pause.
 *
 * Zwischen zwei Dateien fehlt die Aufzeichnung - warum, steht nicht darin. Zwei
 * Fälle gibt es, und sie werden unterschieden wie innerhalb einer Datei
 * (siehe die Anmerkung zu GpxFile.points): stand das Fahrzeug, ist es ein Halt;
 * ging es weiter, zählt die Strecke.
 *
 * Das ist genau der Mittagshalt, um den es geht. Das Gerät geht nach fünf
 * Minuten Stillstand in Bereitschaft, die Aufzeichnung zerfällt in zwei
 * Dateien - und die Stunde dazwischen ist Standzeit und nicht Fahrzeit. Ohne
 * diese Bruecke wäre sie beides nicht: die Zeit stünde in der Gesamtzeit und
 * fehlte in der Standzeit, also erschiene sie als Fahrzeit.
 *
 * Die Schwellen sind dieselben wie überall (STOP_RADIUS_M, STOP_MIN_SECONDS,
 * MOVE_RADIUS_M): was auf einer Etappe ein Halt ist, ist es auch zwischen zwei
 * Dateien.
 *
 * ## Der dritte Fall: die Zeit, über die nichts gemessen wurde
 *
 * Ist die Lücke kein Halt, ist damit erst gesagt, dass das Fahrzeug den Ort
 * gewechselt hat - nicht, dass es die ganze Zeit gefahren ist. In Olten liegen
 * zwischen zwei Dateien 110 Minuten und 409 Meter: gefahren ist der Wagen ein
 * paar davon, gestanden hat er den Rest, und welche Aufteilung es war, steht in
 * keiner der beiden Dateien. Diese Zeit gehört deshalb weder in die Fahrzeit
 * noch in die Standzeit, sondern in nichtAufgezeichnet - sonst behauptet die
 * Etappe vier Stunden am Steuer, wo zweieinhalb gefahren wurden.
 *
 * Zu kurz für einen Halt (STOP_MIN_SECONDS) heisst zu kurz für diese
 * Unterscheidung: eine Lücke von zwei Minuten wird gezählt wie der Abstand
 * zweier Punkte innerhalb einer Datei, nämlich als Fahrt. Unterhalb dieser
 * Schwelle unterscheidet die App Stehen und Fahren nirgends.
 */
function brueckeZwischen(a: TrackPart, b: TrackPart): Bruecke {
  const von = a.geometry[a.geometry.length - 1]
  const nach = b.geometry[0]
  if (!von || !nach) return { meter: 0, stop: null, nichtAufgezeichnet: 0 }

  const weg = haversineM(von, nach)
  const pause = a.endedAt !== null && b.startedAt !== null ? (b.startedAt - a.endedAt) / 1000 : null

  if (pause !== null && pause >= STOP_MIN_SECONDS && weg <= STOP_RADIUS_M) {
    return {
      meter: 0,
      stop: {
        from: a.endedAt!,
        to: b.startedAt!,
        at: [(von[0] + nach[0]) / 2, (von[1] + nach[1]) / 2],
      },
      nichtAufgezeichnet: 0,
    }
  }

  // Kein Halt: das Fahrzeug hat den Ort gewechselt. Gezählt wird die Sehne, und
  // zwar nach derselben Regel wie im Streckenmesser - unter MOVE_RADIUS_M ist
  // es Rauschen und keine Fahrt. Die Zeit dazwischen ist keine Messung.
  return {
    meter: weg >= MOVE_RADIUS_M ? weg : 0,
    stop: null,
    nichtAufgezeichnet: pause !== null && pause >= STOP_MIN_SECONDS ? Math.round(pause) : 0,
  }
}

/**
 * Wie viele Stützpunkte ein einzelner Teil behalten darf.
 *
 * Die Teile teilen sich das Budget des ganzen Wegs (MAX_TRACK_POINTS). Der
 * Grund steht dort: der Track liegt im Tagebucheintrag, und der geht bei jeder
 * Änderung als Ganzes durch den Abgleich. Ein Tag aus drei Dateien darf
 * deshalb nicht dreimal so schwer sein wie einer aus einer.
 *
 * Was das kostet, ist ausschliesslich die Feinheit der gezeichneten Linie. Die
 * Kilometer, die Zeiten, die Halte und das Profil eines Teils sind beim Import
 * über alle Rohpunkte gerechnet worden und stehen als Zahlen fest; sie werden
 * hier nie nachgerechnet.
 */
function teilBudget(anzahl: number): number {
  return Math.max(2, Math.floor(MAX_TRACK_POINTS / anzahl))
}

/**
 * Legt die Teile eines Tages zu einem Track zusammen.
 *
 * Zeitlich sortiert, mit den Lücken dazwischen als Halt oder als Strecke
 * (siehe brueckeZwischen). Die Zahlen sind Summen über die Teile und keine
 * Neuberechnung: die Rohpunkte gibt es nach dem Import nicht mehr.
 *
 * Die Gesamtzeit ist die Spanne vom ersten bis zum letzten Zeitstempel, nicht
 * die Summe der Teile - sonst fiele die Pause zwischen zwei Dateien aus der
 * Rechnung, und der Tag wäre kürzer, als er war. Die Fahrzeit ist der Rest
 * abzüglich dessen, was gar nicht aufgezeichnet wurde: bei einer einzelnen
 * Datei gibt es das nicht, zwischen zwei Dateien schon.
 *
 * @returns null für eine leere Liste. So sieht eine Etappe aus, von der der
 *   letzte Teil entfernt wurde: sie hat keinen Track mehr, und nicht einen
 *   leeren.
 */
export function mergeTrackParts(teile: TrackPart[]): DiaryTrack | null {
  if (teile.length === 0) return null

  const sortiert = nachFahrtzeit(teile)
  // Ein einzelner Teil ist der Track selbst. Kein parts-Feld, keine Kopie
  // seiner selbst - und wer den vorletzten Teil entfernt, bekommt genau die
  // Form zurück, die ein Import aus einer Datei anlegt.
  if (sortiert.length === 1) return alsTrack(sortiert[0]!)

  const budget = teilBudget(sortiert.length)
  const gekuerzt = sortiert.map((t): TrackPart => ({
    ...t,
    geometry: thinGeometry(t.geometry, budget),
  }))

  const geometry: LatLon[] = []
  const stops: TrackStop[] = []
  const fixes: TrackFix[] = []
  const profil: TrackProfilePoint[] = []
  let meter = 0
  let stehend = 0
  let ungemessen = 0
  let punkte = 0
  let segmente = 0
  let verworfen = 0
  let min: number | null = null
  let max: number | null = null
  let gain = 0

  for (let i = 0; i < gekuerzt.length; i++) {
    const teil = gekuerzt[i]!

    if (i > 0) {
      const bruecke = brueckeZwischen(gekuerzt[i - 1]!, teil)
      meter += bruecke.meter
      ungemessen += bruecke.nichtAufgezeichnet
      if (bruecke.stop) {
        stops.push(bruecke.stop)
        stehend += (bruecke.stop.to - bruecke.stop.from) / 1000
      }
    }

    if (teil.elevation) {
      // Das Profil liegt über der gefahrenen Strecke, nicht über der Zeit -
      // der zweite Teil beginnt also dort, wo der erste aufgehört hat.
      const versatz = meter / 1000
      for (const [km, ele] of teil.elevation.profile) {
        profil.push([Math.round((versatz + km) * 100) / 100, ele])
      }
      min = min === null ? teil.elevation.min : Math.min(min, teil.elevation.min)
      max = max === null ? teil.elevation.max : Math.max(max, teil.elevation.max)
      // Summiert und nicht neu gerechnet: der Anstieg jedes Teils steht mit
      // seiner eigenen Hysterese fest. Was zwischen zwei Teilen an Höhe liegt,
      // bleibt aussen vor - dort fehlt die Aufzeichnung, und eine Stufe von
      // hundert Metern über eine Lücke wäre erfunden.
      gain += teil.elevation.gain
    }

    geometry.push(...teil.geometry)
    stops.push(...teil.stops)
    if (teil.fixes) fixes.push(...teil.fixes)
    stehend += teil.stoppedSeconds
    ungemessen += teil.unrecordedSeconds ?? 0
    meter += teil.distanceKm * 1000
    punkte += teil.pointCount
    segmente += teil.segments
    verworfen += teil.dropped
  }

  let start: number | null = null
  let ende: number | null = null
  for (const teil of gekuerzt) {
    if (teil.startedAt !== null)
      start = start === null ? teil.startedAt : Math.min(start, teil.startedAt)
    if (teil.endedAt !== null) ende = ende === null ? teil.endedAt : Math.max(ende, teil.endedAt)
  }

  const totalSeconds =
    start !== null && ende !== null
      ? Math.max(0, Math.round((ende - start) / 1000))
      : gekuerzt.reduce((sum, t) => sum + t.totalSeconds, 0)
  // Ohne Uhr gibt es keine Spanne, und überlappende Teile könnten mehr
  // Standzeit ergeben, als der Tag lang war.
  const stoppedSeconds = Math.min(totalSeconds, Math.round(stehend))
  // Dieselbe Schranke für die dritte Zeit: eine Lücke ist so lang, wie die
  // Zeitstempel an ihren Rändern sagen, aber was schon als Standzeit gebucht
  // ist, kann nicht noch einmal fehlen - und länger als der Tag wird sie nicht.
  const unrecordedSeconds = Math.min(totalSeconds - stoppedSeconds, Math.round(ungemessen))

  // Die Herkunft ist die des zuletzt eingelesenen Teils: "gelesen am" soll das
  // letzte Mal meinen, nicht das erste. Welche Dateien darin stecken, steht
  // vollständig in parts.
  const neuster = gekuerzt.reduce((a, b) => (b.importedAt >= a.importedAt ? b : a))

  return {
    geometry: thinGeometry(geometry, MAX_TRACK_POINTS),
    parts: gekuerzt,
    distanceKm: Math.round(meter) / 1000,
    startedAt: start,
    endedAt: ende,
    totalSeconds,
    movingSeconds: Math.max(0, totalSeconds - stoppedSeconds - unrecordedSeconds),
    stoppedSeconds,
    // Nur wo es sie gibt. Ein Tag, dessen Dateien lückenlos aneinander
    // anschliessen, trägt kein Feld mit einer Null.
    ...(unrecordedSeconds > 0 ? { unrecordedSeconds } : {}),
    stops: kuerzeStopps([...stops].sort((a, b) => a.from - b.from)),
    ...(fixes.length > 0 ? { fixes: legeFixesZusammen(fixes) } : {}),
    ...(min !== null && max !== null
      ? {
          elevation: {
            profile: thinGeometry(profil, MAX_PROFILE_POINTS, 'profile'),
            min,
            max,
            gain: Math.round(gain),
          },
        }
      : {}),
    pointCount: punkte,
    segments: segmente,
    dropped: verworfen,
    fileName: neuster.fileName,
    importedAt: neuster.importedAt,
  }
}

/**
 * Überschneidung zweier Zeitbereiche in Sekunden, höchstens 0 ohne.
 *
 * Teile ohne Uhr haben keinen Bereich und überschneiden sich mit nichts.
 */
function ueberschneidung(a: TrackPart, b: TrackPart): number {
  if (a.startedAt === null || a.endedAt === null || b.startedAt === null || b.endedAt === null) {
    return 0
  }
  return (Math.min(a.endedAt, b.endedAt) - Math.max(a.startedAt, b.startedAt)) / 1000
}

/**
 * Überschneidung, die noch als Zufall durchgeht.
 *
 * Eine Minute. Zwei Dateien, die aneinander anschliessen, teilen sich
 * gelegentlich den Punkt an der Naht oder ein paar Sekunden davon - das ist
 * keine doppelte Fahrt. Eine echte Wiederholung ist um Grössenordnungen
 * länger: sie ist dieselbe Fahrt.
 */
export const OVERLAP_TOLERANCE_SECONDS = 60

/** Was einem neuen Teil im Weg steht. */
export interface TrackKonflikt {
  /**
   * 'gleich' ist dieselbe Aufzeichnung, Punkt für Punkt. 'überlappt' ist eine
   * andere Datei über denselben Zeitraum - eine Teilmenge, eine Obermenge oder
   * dieselbe Fahrt in einer anderen Auflösung.
   */
  art: 'gleich' | 'überlappt'
  /** Der Teil, mit dem es sich beisst. */
  teil: TrackPart
  /** Überschneidung in Sekunden. Bei 'gleich' die des gleichen Bereichs. */
  sekunden: number
}

/**
 * Prüft, ob ein Teil schon einmal da war.
 *
 * Das ist die eigentliche Arbeit am Zusammenlegen: zwei Tracks aneinander zu
 * hängen ist leicht, aber ein zweimal eingelesener Vormittag zählt zweimal in
 * die Kilometer, und die Zahl sieht danach genauso selbstbewusst aus wie eine
 * richtige.
 *
 * Entschieden wird am Zeitbereich und nicht am Dateinamen: der Name wiederholt
 * sich, das Gerät vergibt ihn nach Datum und Nummer neu. Auch nicht an der
 * Punktzahl: dasselbe Stück Strasse hat bei 1 Hz zehnmal so viele Punkte wie
 * bei alle 10 s, und der Columbus kann obendrein nach Distanz aufzeichnen. Der
 * Zeitbereich dagegen ist genau das, woran die Kilometer hängen - eine Stunde
 * Fahrt, die zweimal in der Summe steht, ist genau dann doppelt, wenn dieselbe
 * Stunde zweimal dasteht. Der Fingerabdruck (fingerprintOf) kommt dazu, aber
 * nur, um die Meldung genauer zu machen: er erkennt dieselbe Datei, nicht
 * dieselbe Fahrt aus einem zweiten Export.
 *
 * @returns null, wenn nichts dagegen spricht.
 */
export function trackKonflikt(vorhandene: TrackPart[], neu: TrackPart): TrackKonflikt | null {
  for (const teil of vorhandene) {
    if (teil.fingerprint !== '' && teil.fingerprint === neu.fingerprint) {
      return { art: 'gleich', teil, sekunden: Math.max(0, ueberschneidung(teil, neu)) }
    }
  }

  for (const teil of vorhandene) {
    const sekunden = ueberschneidung(teil, neu)
    if (sekunden > OVERLAP_TOLERANCE_SECONDS) return { art: 'überlappt', teil, sekunden }
  }

  return null
}

/** Das Ergebnis eines Imports: der Track und, was dazu zu sagen ist. */
export interface GpxImport {
  track: DiaryTrack
  /**
   * Ein Satz für die Anzeige, wenn die Datei nur zum Teil gelesen werden
   * konnte, sonst null.
   *
   * Warum eine Warnung und kein Fehler: kaputtes Markup am Ende einer Datei
   * macht die neunzig Prozent davor nicht falsch. Wer im Auto sitzt und den
   * Tag einträgt, ist mit einer Fahrzeit, die den Vormittag richtig hat und
   * dazusagt, dass der Nachmittag fehlt, besser bedient als mit einer
   * abgewiesenen Datei und gar nichts. Umgekehrt darf die Zahl nicht ohne
   * diesen Satz dastehen - dann wäre sie eine Behauptung.
   */
  warning: string | null
}

/**
 * Der ganze Weg von der Datei zum Tagebuch.
 *
 * @throws GpxError wenn die Datei kein GPX ist, keinen Track enthält oder aus
 *   einem einzigen Punkt besteht - eine Strecke braucht zwei.
 */
export function trackFromGpx(xml: string, fileName: string, importedAt: number): GpxImport {
  const datei = parseGpx(xml)
  if (datei.points.length < 2) {
    throw new GpxError(
      datei.truncated
        ? `${abbruchSatz(datei.truncated)} und enthält davor nur einen einzigen Punkt, daraus wird keine Strecke.`
        : 'Die Datei enthält nur einen einzigen Punkt, daraus wird keine Strecke.',
    )
  }
  return {
    track: deriveTrack(datei.points, {
      fileName,
      importedAt,
      segments: datei.segments,
      dropped: datei.dropped,
    }),
    warning: warnungAus([
      datei.truncated ? truncationWarning(datei.truncated) : null,
      zeitWarnung(datei.points, importedAt),
    ]),
  }
}

// --- Eine Datei über mehrere Tage -------------------------------------------

/**
 * Wie lange die Pause sein muss, die zwei Fahrtage trennt. Vier Stunden.
 *
 * Warum überhaupt eine Pause und nicht die Uhr: Mitternacht ist die einfache
 * Antwort und für eine Fahrt, die um 23:40 Ortszeit endet, die falsche - sie
 * schnitte den Tag mitten auf der Autobahn entzwei und legte zwanzig Minuten
 * Fahrt auf die Etappe des Folgetags. Mitternacht welcher Zone wäre die
 * nächste Frage: die Reise geht durch zwei, und die Datei sagt nicht, in
 * welcher sie geschrieben wurde. Ein Fahrtag hört nicht um eine bestimmte
 * Uhrzeit auf, sondern dort, wo das Fahrzeug für die Nacht steht - und das
 * steht in der Aufzeichnung.
 *
 * Warum vier Stunden und nicht acht: eine Nacht ist länger, aber nicht immer
 * lang. Wer um 23:00 ankommt und um 5:00 weiterfährt, hat sechs Stunden; wer
 * frei steht, auch mal weniger. Nach unten kostet ein zu kleiner Wert dagegen
 * fast nichts: schneidet die Pause einen Tag zu früh, fallen beide Stücke auf
 * dasselbe Datum, landen auf derselben Etappe und werden dort wieder
 * zusammengelegt - mitsamt der Pause als Halt (siehe brueckeZwischen). Ein zu
 * grosser Wert dagegen legt zwei Fahrtage auf eine Etappe, und das fällt erst
 * auf, wenn die Kilometer nicht stimmen. Vier Stunden sind der Wert, bei dem
 * der teure Fehler nicht mehr vorkommt.
 */
export const DAY_BREAK_SECONDS = 4 * 3600

/**
 * Schneidet eine Aufzeichnung an den langen Pausen in Fahrtage.
 *
 * Zwei Formen von Pause gibt es, und beide zählen. Die eine ist ein Halt mit
 * Punkten: das Gerät lief die Nacht über weiter und hat zwölf Stunden
 * Rauschen auf dem Stellplatz aufgezeichnet. Die andere ist eine Lücke ohne
 * Punkte: das Gerät ging nach fünf Minuten Stillstand in Bereitschaft und
 * meldete sich am Morgen wieder. Die erste findet findStops, die zweite steht
 * im Abstand zweier aufeinanderfolgender Zeitstempel.
 *
 * Was innerhalb der langen Pause liegt, gehört zu keinem der beiden Tage: der
 * Punkt der Ankunft bleibt beim ersten, der Punkt der Abfahrt beginnt den
 * zweiten, das Rauschen dazwischen fällt weg. Sonst trägt ein Fahrtag zwölf
 * Stunden Standzeit, die nur der Schlaf waren, und die Zeile "Unterwegs 22 h"
 * über einer Etappe, die um 18:30 zu Ende war.
 *
 * Punkte ohne Zeitstempel bleiben beim laufenden Stück: ohne Uhr gibt es
 * keinen Tag, an dem man schneiden könnte.
 *
 * @returns Die Stücke in zeitlicher Reihenfolge. Stücke mit weniger als zwei
 *   Punkten fallen heraus - daraus wird keine Strecke.
 */
export function splitAtBreaks(points: GpxPoint[], minSeconds = DAY_BREAK_SECONDS): GpxPoint[][] {
  const minMs = minSeconds * 1000
  const lange = findStops(points).filter((s) => s.to - s.from >= minMs)

  const stuecke: GpxPoint[][] = []
  let laufend: GpxPoint[] = []
  let letzte: number | null = null
  let naechster = 0

  const abschliessen = () => {
    if (laufend.length > 0) stuecke.push(laufend)
    laufend = []
    letzte = null
  }

  for (const p of points) {
    if (p.time !== null) {
      // Derselbe vorwärtslaufende Zeiger wie im Streckenmesser: die Halte
      // stehen in zeitlicher Reihenfolge, jeder wird einmal angesehen.
      while (naechster < lange.length && lange[naechster]!.to < p.time) naechster++
      const halt = lange[naechster]

      if (halt && p.time > halt.from && p.time < halt.to) {
        abschliessen()
        continue
      }
      if (letzte !== null && p.time - letzte >= minMs) abschliessen()
      letzte = p.time
    }
    laufend.push(p)
  }
  abschliessen()

  return stuecke.filter((s) => s.length >= 2)
}

/** Eine Datei, in Fahrtage zerlegt. */
export interface GpxTageslese {
  /** Ein fertiger Track je Fahrtag, in zeitlicher Reihenfolge. */
  tage: DiaryTrack[]
  /** Wie bei GpxImport: was zu einer nur halb gelesenen Datei zu sagen ist. */
  warning: string | null
  /** Punkte, die in einer langen Pause lagen und zu keinem Tag gehören. */
  pausen: number
  /** Punkte, die beim Lesen als unbrauchbar übersprungen wurden. */
  dropped: number
}

/**
 * Liest eine Datei und macht daraus einen Track je Fahrtag.
 *
 * Für den Fall, den das Gerät erst möglich macht: 50 Stunden Laufzeit und
 * 8 GB Speicher heissen, dass die halbe Reise in einer einzigen Datei stehen
 * kann. Die ganze Datei auf die Etappe zu legen, bei der man gerade steht,
 * wäre sechzehn Etappen Arbeit und eine falsche Zahl.
 *
 * Zugeordnet wird hier nichts - das braucht die Etappen und die Zustimmung des
 * Nutzers und steht in features/diary/verteilen.ts.
 *
 * @throws GpxError wie trackFromGpx, wenn die Datei kein GPX ist oder nichts
 *   Brauchbares enthält.
 */
export function tracksByDay(
  xml: string,
  fileName: string,
  importedAt: number,
  minSeconds = DAY_BREAK_SECONDS,
): GpxTageslese {
  const datei = parseGpx(xml)
  const stuecke = splitAtBreaks(datei.points, minSeconds)

  if (stuecke.length === 0) {
    throw new GpxError(
      datei.truncated
        ? `${abbruchSatz(datei.truncated)} und enthält davor kein Stück Fahrt aus mehr als einem Punkt.`
        : 'Die Datei enthält kein Stück Fahrt aus mehr als einem Punkt.',
    )
  }

  const genutzt = stuecke.reduce((sum, s) => sum + s.length, 0)

  return {
    tage: stuecke.map((punkte) =>
      // segments und dropped sind Eigenschaften der ganzen Datei und nicht
      // dieses Tages. Sie hier zu verteilen hiesse, sie zu erfinden; sie stehen
      // stattdessen oben in der Lese und werden dort angezeigt.
      deriveTrack(punkte, { fileName, importedAt }),
    ),
    warning: warnungAus([
      datei.truncated ? truncationWarning(datei.truncated) : null,
      zeitWarnung(datei.points, importedAt),
    ]),
    pausen: datei.points.length - genutzt,
    dropped: datei.dropped,
  }
}

/**
 * Die Punkte, die für diesen Tagebucheintrag gezeichnet werden dürfen.
 *
 * @returns null, wenn kein brauchbarer Track vorliegt. Ein Weg aus einem Punkt
 *   ist keine Linie.
 */
export function trackGeometry(entry: DiaryEntry | undefined): LatLon[] | null {
  const geometry = entry?.track?.geometry
  if (!Array.isArray(geometry) || geometry.length < 2) return null
  return geometry
}
